import { useRouter } from 'next/router';
import React, { useCallback, useEffect, useRef } from 'react';
import { SubmitHandler, useFormContext } from 'react-hook-form';
import { ObjectSchema } from 'yup';

import { Button, NotificationManager, Space } from '@sravni/react-design-system';
import { Link, Text } from '@sravni/react-design-system/lib/Typography';
import { useBoolean, useIsPhone, useLocalStorage, useStyles } from '@sravni/react-utils';

import { NOTIFICATION_TIMEOUT } from '../../../../constants/common';
import { ENTITY_TYPE } from '../../../../constants/reviews';
import { useOnFingerprintCreate } from '../../../../hooks/useFingerprint';
import { useIsUserLogged } from '../../../../store/hooks/users';
import { TCreateReviewPageQuery } from '../../../../types/router';
import { FormControl } from '../../../UI/FormControl';
import { TUserChangeHandler } from '../../../UI/FormControl/types';
import { DeleteReview } from '../../../UserActions/DeleteReview';
import { REVIEW_FIELDS } from '../../constants/common';
import { checkStoredReviewMatchingQuery } from '../../helpers/checkStoredReviewMatchingQuery';
import { useHandleChanges } from '../../hooks/useHandleChanges';
import { TFormInputs } from '../../types';
import { AuthorizationStep } from '../Steps/Authorization/AuthorizationStep';
import { TDefaultValuesForAuthorization } from '../Steps/Authorization/interfaces';
import { ExtraInfoStep } from '../Steps/ExtraInfo/ExtraInfoStep';
import { OrganizationAndServiceStep } from '../Steps/OrganizationAndService/OrganizationAndServiceStep';
import { RatingStep } from '../Steps/Rating/RatingStep';
import { TextStep } from '../Steps/Text/TextStep';

import styles from './main-form.module.scss';

export interface IMainFormProps {
  onChange: (values: TFormInputs, changedKeys: Array<keyof TFormInputs>) => void;
  onSubmit: (data: TFormInputs) => Promise<void>;
  handleDelete: () => void;
  isRequiredUserData: boolean;
  isReadOnly: boolean;
  isExtraInfoReadOnly: boolean;
  isRatingReadOnly: boolean;
  isDeleteButtonVisible: boolean;
  isEdit: boolean;
  isHidden: boolean;
  handleUserChange?: TUserChangeHandler;
  commonEventLabel: string[];
  schema: ObjectSchema<TFormInputs>;
  handleSuccessAuth?: () => void;
  handleFormReady?: () => void;
  isFormReadyToShow: boolean;
  defaultValuesForAuthorization: TDefaultValuesForAuthorization;
}

export const MainForm: React.FC<IMainFormProps> = (props) => {
  const {
    onChange,
    onSubmit,
    handleDelete,
    isReadOnly,
    isExtraInfoReadOnly,
    isRatingReadOnly,
    isDeleteButtonVisible,
    isEdit,
    isHidden,
    handleUserChange,
    commonEventLabel,
    schema,
    handleSuccessAuth,
    handleFormReady,
    isFormReadyToShow,
    defaultValuesForAuthorization,
  } = props;
  const { query } = useRouter() as TRouter<TCreateReviewPageQuery>;
  const cx = useStyles(styles);
  const isPhone = useIsPhone();
  const extraInfoRef = useRef<HTMLDivElement>();
  const isUserLogged = useIsUserLogged();

  const [isAuthorizationBlockVisible, setAuthorizationBlockVisibility] = useBoolean(false);
  const [storedReview, setStoredReview] = useLocalStorage<Partial<TFormInputs>>('preparedReview', null);

  const {
    handleSubmit,
    formState: { errors, isSubmitting, isDirty },
    setValue,
    getValues,
    control,
  } = useFormContext<TFormInputs>();

  useEffect(() => {
    if (query.scrollToExtraInfo && isFormReadyToShow) {
      const topCoordinate = extraInfoRef?.current?.getBoundingClientRect().top + window.scrollY - 30;
      window.scrollTo({ behavior: 'smooth', top: topCoordinate });
    }
  }, [query, isFormReadyToShow]);

  const isStoredReviewMatchingQuery = checkStoredReviewMatchingQuery(query, storedReview);
  const needToRestore = isStoredReviewMatchingQuery && storedReview && !isEdit;
  const isRestoring = useRef(needToRestore);

  useHandleChanges({
    isRestoring,
    isEdit,
    storedReview,
    setStoredReview,
    onChange,
  });

  const checkUserAndSubmit: SubmitHandler<TFormInputs> = useCallback(
    async (data) => {
      if (!isUserLogged) {
        setAuthorizationBlockVisibility.on();
        return;
      }

      try {
        await onSubmit(data);
        setStoredReview(null);
      } catch (_) {
        setValue(REVIEW_FIELDS.IS_USER_READY_TO_SUBMIT, false);
      }
    },
    [isUserLogged, onSubmit, setAuthorizationBlockVisibility, setStoredReview, setValue],
  );

  const validateAndSubmit = useCallback(
    (onValid?: () => void, onInvalid?: () => void) => {
      const handleValid: SubmitHandler<TFormInputs> = async (data) => {
        await checkUserAndSubmit(data);
        onValid?.();
      };

      const handleInvalid = (errors) => {
        console.error(errors);
        onInvalid?.();
      };

      return handleSubmit(handleValid, handleInvalid)();
    },
    [checkUserAndSubmit, handleSubmit],
  );

  const handleFormSubmitByUser = useCallback(async () => {
    if (isEdit && !isDirty) {
      NotificationManager.show({
        message: 'Внесите изменения',
        timeout: NOTIFICATION_TIMEOUT,
      });
      return;
    }
    setValue(REVIEW_FIELDS.IS_USER_READY_TO_SUBMIT, true);
    await validateAndSubmit();
  }, [isDirty, isEdit, setValue, validateAndSubmit]);

  useOnFingerprintCreate(async () => {
    const { [REVIEW_FIELDS.IS_USER_READY_TO_SUBMIT]: isUserReadyToSubmit } = getValues();

    if (!isEdit && isUserLogged && isDirty && isUserReadyToSubmit) {
      await validateAndSubmit();
    } else {
      handleFormReady?.();
    }
  });

  const onChangeAgreement = useCallback(
    (value: boolean) => {
      setValue(REVIEW_FIELDS.AGREEMENT, value, {
        shouldDirty: false,
        shouldValidate: false,
        shouldTouch: false,
      });
    },
    [setValue],
  );

  const onClickSubmitAuthButton = useCallback(() => {
    setValue(REVIEW_FIELDS.IS_USER_READY_TO_SUBMIT, true);
  }, [setValue]);

  return (
    <Space direction={'vertical'} size={isPhone ? 32 : 48} className={cx('formWrapper', { hidden: isHidden })}>
      <form onSubmit={handleFormSubmitByUser}>
        <Space direction={'vertical'} size={isPhone ? 32 : 48}>
          <RatingStep isReadOnly={isRatingReadOnly} handleUserChange={handleUserChange} />
          <OrganizationAndServiceStep isReadOnly={isReadOnly} handleUserChange={handleUserChange} />
          <TextStep handleUserChange={handleUserChange} schema={schema} isReadOnly={isReadOnly} />
          <ExtraInfoStep
            extraInfoRef={extraInfoRef}
            isReadOnly={isExtraInfoReadOnly}
            handleUserChange={handleUserChange}
            commonEventLabel={commonEventLabel}
          />

          {!isAuthorizationBlockVisible && (
            <div>
              <Space
                direction={isPhone ? 'vertical' : 'horizontal'}
                size={32}
                align={'center'}
                justify={'space-between'}
              >
                <Button
                  onClick={handleFormSubmitByUser}
                  className={cx('submitButton')}
                  size={60}
                  block={isPhone}
                  variant={'primary'}
                  disabled={isSubmitting}
                >
                  {isEdit ? 'Сохранить изменения' : 'Опубликовать отзыв'}
                </Button>
                {isDeleteButtonVisible && <DeleteReview onDelete={handleDelete} type={ENTITY_TYPE.REVIEW} />}
              </Space>

              <FormControl
                controlType="checkbox"
                name={REVIEW_FIELDS.AGREEMENT}
                control={control}
                label={
                  <Text size={12}>
                    Нажимая кнопку, вы даёте согласие на{'\u00A0'}
                    <Link href="https://www.sravni.ru/promo/review-rules/" target={'_blank'} rel={'noreferrer'}>
                      публикацию отзыва
                    </Link>{' '}
                    и{' '}
                    <Link
                      href="https://www.sravni.ru/promo/soglasie-na-obrabotku-personalnyh-dannyh/"
                      target={'_blank'}
                      rel={'noreferrer'}
                    >
                      обработку данных
                    </Link>
                  </Text>
                }
                error={errors?.[REVIEW_FIELDS.AGREEMENT]}
                disabled={isSubmitting}
                className={'h-mt-24'}
              />
            </div>
          )}
        </Space>
      </form>
      {isAuthorizationBlockVisible && (
        <AuthorizationStep
          defaultValues={defaultValuesForAuthorization}
          onChangeAgreement={onChangeAgreement}
          onClickSubmitAuthButton={onClickSubmitAuthButton}
          eventLabel={commonEventLabel}
          onSuccessAuth={handleSuccessAuth}
        />
      )}
    </Space>
  );
};
